import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type StripeElementsOptions } from "@stripe/stripe-js";
import React, { useMemo } from "react";

type StripeElementsWrapperProps = React.PropsWithChildren & {
  connectedAccountId: string;
  /**
   * Paired storefronts render widgets before Stripe is connected: Elements
   * mounts on the platform key so Stripe hooks work, but nothing is charged.
   */
  allowMissingAccount?: boolean;
  stripeRegion?: "CA" | "US";
  stripeTestMode?: boolean;
  elementsOptions?: StripeElementsOptions;
};

export const StripeElementsWrapper = ({
  connectedAccountId,
  allowMissingAccount,
  stripeRegion = "CA",
  stripeTestMode,
  elementsOptions,
  children,
}: StripeElementsWrapperProps) => {
  const publishableKey =
    stripeRegion === "US"
      ? stripeTestMode
        ? import.meta.env.VITE_STRIPE_US_TEST_PUBLISHABLE_KEY
        : import.meta.env.VITE_STRIPE_US_PUBLISHABLE_KEY
      : stripeTestMode
        ? import.meta.env.VITE_STRIPE_TEST_PUBLISHABLE_KEY
        : import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

  const hasAccount = !!connectedAccountId && connectedAccountId.length > 0;
  if (!publishableKey || (!hasAccount && !allowMissingAccount)) {
    throw new Error("Missing stripe publishable key or connected account id");
  }

  const stripePromise = useMemo(
    () =>
      hasAccount
        ? loadStripe(publishableKey, { stripeAccount: connectedAccountId })
        : loadStripe(publishableKey),
    [connectedAccountId, hasAccount, publishableKey],
  );
  const elementsKey = useMemo(
    () =>
      JSON.stringify({
        connectedAccountId,
        publishableKey,
        elementsOptions,
      }),
    [connectedAccountId, elementsOptions, publishableKey],
  );

  return (
    <Elements
      key={elementsKey}
      options={{
        ...elementsOptions,
        fonts: [
          {
            cssSrc:
              "https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600&display=swap",
          },
        ],
      }}
      stripe={stripePromise}
    >
      {children}
    </Elements>
  );
};
